import { FC, useEffect, useState } from "react"
import { observer } from "mobx-react"
import { useStore } from "stores/utils/use-store"
import { Button, Card, Form, Input, InputNumber, Select, Space, Table, Tag, Typography } from "antd"
import { MatchDecision, PairwiseVerdict } from "../store/types"

const DECISIONS: MatchDecision[] = ["SAME", "DIFFERENT", "UNSURE"]
const DECISION_COLOR: Record<MatchDecision, string> = {
  SAME: "green",
  DIFFERENT: "red",
  UNSURE: "gold",
}

const DEFAULT_LIMIT = 50

export const PairwiseVerdictsList: FC = observer(() => {
  const { dataStore: { matchClusteringStore } } = useStore()

  const [filters, setFilters] = useState<{ gameId?: number; decision?: MatchDecision; recordingId?: string }>({})

  useEffect(() => {
    // Initial fetch only — filter changes go through Apply / Reset, so this
    // effect must not depend on `filters` (would refetch on every keystroke).
    matchClusteringStore.loadPairwiseVerdicts({ offset: 0, limit: DEFAULT_LIMIT })
  }, [matchClusteringStore])

  const applyFilters = () => {
    matchClusteringStore.loadPairwiseVerdicts({ ...filters, offset: 0, limit: DEFAULT_LIMIT })
  }

  const columns = [
    { title: "Recording A", dataIndex: "recordingIdA", key: "recordingIdA" },
    { title: "Recording B", dataIndex: "recordingIdB", key: "recordingIdB" },
    {
      title: "Decision",
      dataIndex: "decision",
      key: "decision",
      width: 110,
      render: (d: MatchDecision) => <Tag color={DECISION_COLOR[d]}>{d}</Tag>,
    },
    {
      title: "Reasons",
      dataIndex: "reasons",
      key: "reasons",
      render: (rs: string[]) => (rs.length ? <Typography.Text>{rs.join(", ")}</Typography.Text> : "—"),
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 180,
      render: (v: string) => new Date(v).toLocaleString(),
    },
  ]

  return (
    <Card title="Pairwise verdicts">
      <Form layout="inline" style={{ marginBottom: 16 }}>
        <Form.Item label="Game ID">
          <InputNumber
            value={filters.gameId}
            onChange={v => setFilters({ ...filters, gameId: v ?? undefined })}
            placeholder="any"
          />
        </Form.Item>
        <Form.Item label="Decision">
          <Select
            value={filters.decision}
            onChange={v => setFilters({ ...filters, decision: v })}
            options={DECISIONS.map(d => ({ value: d, label: d }))}
            style={{ minWidth: 140 }}
            placeholder="any"
            allowClear
          />
        </Form.Item>
        <Form.Item label="Recording">
          <Input
            value={filters.recordingId}
            onChange={e => setFilters({ ...filters, recordingId: e.target.value || undefined })}
            placeholder="UUID"
            style={{ width: 280 }}
            allowClear
          />
        </Form.Item>
        <Form.Item>
          <Space>
            <Button type="primary" onClick={applyFilters}>Apply</Button>
            <Button
              onClick={() => {
                setFilters({})
                matchClusteringStore.loadPairwiseVerdicts({ offset: 0, limit: DEFAULT_LIMIT })
              }}
            >
              Reset
            </Button>
          </Space>
        </Form.Item>
      </Form>
      <Table<PairwiseVerdict>
        rowKey="id"
        dataSource={matchClusteringStore.verdicts.slice()}
        columns={columns}
        loading={matchClusteringStore.isLoading}
        pagination={{
          total: matchClusteringStore.verdictsTotal,
          pageSize: matchClusteringStore.currentVerdictQuery.limit ?? DEFAULT_LIMIT,
          current:
            Math.floor((matchClusteringStore.currentVerdictQuery.offset ?? 0)
              / (matchClusteringStore.currentVerdictQuery.limit ?? DEFAULT_LIMIT)) + 1,
          onChange: (page, pageSize) => {
            matchClusteringStore.loadPairwiseVerdicts({ ...filters, offset: (page - 1) * pageSize, limit: pageSize })
          },
        }}
      />
    </Card>
  )
})
